import { Box } from '@mui/material'
import styled from '@emotion/styled/macro'
import theme from 'theme'
import { INTEGRATIONS_HEADER_HEIGHT } from '../../../components/header/integrationHeader.styles'
import { TestStatus } from './SetupIntegrationInstance'
import { disabledStyles } from '../../../components/buttons'

export const IntegrationsContainer = styled(Box)<{ hasPadding?: boolean }>`
  width: 100%;
  height: 100%;

  form {
    height: 100%;
    overflow-y: hidden;
    max-height: calc(100vh - ${INTEGRATIONS_HEADER_HEIGHT}px);
  }
`

export const EditIntegrationContainer = styled(Box)<{ testStatus: TestStatus; isopen?: string }>`
  display: flex;
  gap: ${theme.spacing.medium}px;
  height: 100%;
  max-height: calc(100vh - ${INTEGRATIONS_HEADER_HEIGHT}px);
  width: 100%;
  background: ${theme.colors.pageBackground};

  /* Password visibility toggle glyph, matching the Figma Icon/secondary. */
  html[data-theme='light'] & .MuiInputAdornment-root .MuiIconButton-root svg path {
    fill: ${theme.colors.selectChevron};
  }

  ${({ isopen }) =>
    isopen === 'false' &&
    `
      justify-content: center;
  `};

  .left {
    width: 100%;
    flex-basis: 50%;
    overflow-y: auto;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    flex-direction: column;
    padding: 0 ${theme.spacing.medium}px;

    ${({ isopen }) =>
      isopen === 'false' &&
      `
      flex-basis: 100%;
      max-width: 752px;
  `};

    .info {
      display: flex;
      flex-flow: column wrap;
      width: 100%;
      gap: ${theme.spacing.medium}px;

      .connection-details-wrapper {
        padding-top: ${theme.spacing.medium}px;
        display: flex;
        align-items: center;
        justify-content: space-between;
        width: 100%;

        .connection-details-description {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          justify-content: center;
          gap: ${theme.spacing.md}px;

          .connection-details-title {
            white-space: nowrap;
            color: ${theme.colors.menuItemText};
            font-family: ${theme.typography.fontFamily.primary};
            font-size: ${theme.typography.fontSize.xxxLarge};
            font-weight: ${theme.typography.fontWeight.bold};
            font-style: normal;
            line-height: 36px;
            letter-spacing: 0.1px;
          }

          .connection-details-text {
            color: ${theme.colors.devicesSecondaryText};
            font-family: ${theme.typography.fontFamily.primary};
            font-size: ${theme.typography.fontSize.large};
            font-weight: ${theme.typography.fontWeight.normal};
            font-style: normal;
            line-height: 20px;
            letter-spacing: 0.25px;
            white-space: nowrap;
          }
        }

        .connection-details-action {
          .expand-info-button {
            border-radius: 12px !important;
            width: 48px !important;
            height: 48px !important;
            padding: 0 !important;
            min-width: unset !important;
            background: ${theme.colors.btnTertiaryBg} !important;
            border: 1px solid transparent;
          }
        }
      }

      .MuiInputLabel-root {
        color: ${theme.colors.foreground};
        font-size: ${theme.typography.fontSize.xLarge};
        font-style: normal;
        font-weight: ${theme.typography.fontWeight.normal};
        line-height: normal;
      }

      .MuiOutlinedInput-root {
        border-radius: ${theme.attributes.borderRadius.tertiary};
        height: 48px;
        padding: 0 ${theme.spacing.small}px;
        border: 0 !important;

        &.MuiInputBase-multiline {
          height: 96px;
        }

        .MuiOutlinedInput-input {
          overflow: hidden;
          text-overflow: ellipsis;
          padding: 0;
          color: ${theme.colors.menuItemText};
          -webkit-text-fill-color: ${theme.colors.menuItemText};
          font-family: ${theme.typography.fontFamily.primary};
          font-size: ${theme.typography.fontSize.large};
          font-weight: ${theme.typography.fontWeight.bold};
          font-style: normal;
          line-height: 20px;
          letter-spacing: 0.25px;

          &.MuiInputBase-inputMultiline {
            margin-top: ${theme.spacing.xSmall}px;
          }
        }

        fieldset {
          border-radius: ${theme.attributes.borderRadius.tertiary};
          border: 1px solid ${theme.colors.dropdownBorder};
          padding: 0 ${theme.spacing.xSmall}px;
        }
      }

      .MuiInputAdornment-root {
        svg {
          path {
            fill: #debdd9;

            html[data-theme='light'] & {
              fill: ${theme.colors.devicesSecondaryText};
            }
          }
        }
      }
    }

    .actions {
      display: flex;
      align-items: flex-end;
      justify-content: space-between;
      height: auto;
      width: 100%;
      padding-top: ${theme.spacing.lg}px;

      ${({ testStatus }) =>
        testStatus === TestStatus.successful &&
        `
            /* Only where the test button is the first child; a layout that groups
               its buttons in a wrapper opts out and colours them itself. */
            &:not(.grouped-actions) .MuiButtonBase-root:first-of-type {
              color: ${theme.colors.primaryDark};
            }
            

            
            &.Mui-disabled {
              color: ${theme.colors.btnSecondaryDisabledText} !important;

                svg {
                  path {
                    fill: ${theme.colors.btnSecondaryDisabledText} !important;
                  }
                }
            }
        `};
    }

    ::-webkit-scrollbar {
      width: 4px;
      background: transparent;
    }

    ::-webkit-scrollbar-track {
      box-shadow: none;
      background: ${theme.colors.borderLow};
    }

    ::-webkit-scrollbar-thumb {
      background-color: ${theme.colors.cta};
      border-radius: ${theme.attributes.borderRadius.quaternary};
    }
  }

  .right {
    width: 100%;
    flex-basis: 50%;
    padding: ${theme.spacing.medium}px ${theme.spacing.medium}px;
    display: flex;
    flex-flow: column;
    overflow-y: auto;
    height: 100%;
    border-left: 1px solid ${theme.colors.borderLow};
    transition: all 0.2s ease-in-out;

    ::-webkit-scrollbar {
      width: 13px !important;
      background: transparent;
    }

    ::-webkit-scrollbar-track {
      box-shadow: none;
      background: transparent;
    }

    ::-webkit-scrollbar-thumb {
      background-color: ${theme.colors.borderMedium};
      border-radius: ${theme.attributes.borderRadius.quaternary};
    }

    .right-text-wrapper {
      display: flex;
      flex-direction: column;
      gap: ${theme.spacing.sm}px;
      align-items: flex-start;
      margin-bottom: ${theme.spacing.small}px;

      .instructions-text {
        color: ${theme.colors.menuItemText};
        font-family: ${theme.typography.fontFamily.primary};
        font-size: ${theme.typography.fontSize.xxxLarge};
        font-weight: ${theme.typography.fontWeight.bold};
        font-style: normal;
        line-height: 36px;
        letter-spacing: 0.1px;
      }

      .instructions-description {
        color: ${theme.colors.devicesSecondaryText};
        font-family: ${theme.typography.fontFamily.primary};
        font-size: ${theme.typography.fontSize.large};
        font-weight: ${theme.typography.fontWeight.normal};
        font-style: normal;
        line-height: 20px;
        letter-spacing: 0.25px;
      }
    }

    .instructions-inner {
      padding: ${theme.spacing.medium}px 0;
      height: fit-content;
      flex: 1 1 auto;
      width: 100%;
      border-bottom: 1px solid ${theme.colors.borderLow};
      display: flex;
      align-items: flex-start;
      gap: ${theme.spacing.small}px;
      flex-direction: column;

      @media (max-width: 1000px) {
        flex-direction: column;
      }

      .empty-image {
        width: 160px;
        min-width: 160px;
        height: 100%;
      }

      .step-text {
        color: ${theme.colors.cta};
        font-size: 18px;
        font-style: normal;
        font-weight: ${theme.typography.fontWeight.thin};
        line-height: normal;
        margin: 0;
      }

      .instructions-right-side {
        display: flex;
        flex-direction: column;
        justify-content: flex-start;
        width: 100%;

        .section-list-item:first-of-type {
          margin-top: 16px;
        }

        a {
          color: ${theme.colors.cta} !important;

          &:visited,
          &:focus,
          &:focus-visible,
          &:active,
          &:default {
            color: ${theme.colors.cta} !important;
          }
        }

        p {
          margin: 0;
          color: ${theme.colors.menuItemText};
          font-family: ${theme.typography.fontFamily.primary};
          font-size: ${theme.typography.fontSize.xLarge};
          font-style: normal;
          font-weight: ${theme.typography.fontWeight.bold};
          line-height: 24px;
          letter-spacing: 0.1px;
        }

        .section-list-item {
          padding-left: ${theme.spacing.half}px;
          width: 100%;
          color: ${theme.colors.devicesSecondaryText};
          font-family: ${theme.typography.fontFamily.primary};
          font-size: ${theme.typography.fontSize.xLarge};
          font-style: normal;
          font-weight: ${theme.typography.fontWeight.bold};
          line-height: 24px;
          letter-spacing: 0.1px;

          &.sub-item {
            margin-left: ${theme.spacing.medium}px;
          }

          .circle-bullet-list {
            margin-top: ${theme.spacing.half}px;
            margin-bottom: ${theme.spacing.half}px;
          }

          .list-bullet {
            color: ${theme.colors.cta};
          }

          .list-sub-bullet {
            font-weight: ${theme.typography.fontWeight.bold};
          }

          &.inner-item {
            margin-left: 18px;
          }

          & .ip-address {
            background: rgba(135, 131, 120, 0.15);
            color: #eb5757;
            width: fit-content;
            font-size: 85%;
            padding: 0.2em 0.4em;
            border-radius: 4px;
            scrollbar-color: #d3d1cb rgba(0, 0, 0, 0);
          }

          a {
            color: ${theme.colors.cta} !important;

            &:visited,
            &:focus,
            &:focus-visible,
            &:active,
            &:default {
              color: ${theme.colors.cta} !important;
            }
          }
        }

        &.empty-row-instruction {
          > p:nth-of-type(3) {
            display: none !important;
          }
        }
      }

      .instructions-image {
        width: 100%;
        height: 100%;

        &:hover {
          cursor: pointer;
        }
      }

      ol {
        margin: 0;
        padding: 0 0 0 ${theme.spacing.lg}px;
        line-break: auto;

        a {
          color: ${theme.colors.cta};

          &:visited {
            color: ${theme.colors.cta};
          }
        }

        li {
          &::marker {
            color: ${theme.colors.cta};
          }
        }

        li:not(:last-of-type) {
          margin-bottom: ${theme.spacing.small}px;
        }

        li .MuiLink-root {
          color: ${theme.colors.cta};
          text-decoration: underline;
          margin: 0;
          padding: 0;
          font-weight: ${theme.typography.fontWeight.extraThick};
        }

        li {
          ul {
            li {
              margin-bottom: 0 !important;
            }
          }
        }

        .tooltip-icon-padded {
          margin: 8px;
        }

        p {
          margin-top: 0 !important;
          margin-bottom: 0 !important;
        }
      }

      .copy-text {
        font-family: 'IBM Plex Mono', monospace;
        font-style: normal;
        font-weight: ${theme.typography.fontWeight.normal};
        font-size: ${theme.typography.fontSize.large};
        line-height: 20px;
        letter-spacing: 0.25px;
        color: ${theme.colors.cta};
        display: flex;
        align-items: center;
        margin-top: ${theme.spacing.xSmall}px;

        svg {
          margin-left: ${theme.spacing.half}px;
        }
      }
    }
  }
`

export const popperSx = {
  marginTop: `-${theme.spacing.sm}px !important`
}

export const hollowButtonDisabledSx = {
  paddingLeft: '16px !important',
  paddingRight: '16px !important',
  ...disabledStyles.secondary,
  fontWeight: '600 !important',
  lineHeight: 'normal !important',
  svg: {
    path: {
      fill: 'unset !important'
    }
  }
}

export const hollowButtonSx = {
  paddingLeft: `${theme.spacing.small}px !important`,
  paddingRight: `${theme.spacing.small}px !important`,
  fontWeight: `${theme.typography.fontWeight.extraThick} !important`,
  lineHeight: 'normal !important',
  svg: {
    path: {
      fill: 'unset !important',
      stroke: 'currentColor !important'
    }
  },

  '&.Mui-disabled': {
    svg: {
      path: {
        fill: 'unset !important',
        stroke: `${theme.colors.btnSecondaryDisabledText} !important`
      }
    }
  },

  "html[data-theme='light'] &:not(.Mui-disabled):hover": {
    color: `${theme.colors.buttonHollowHover} !important`
  }
}
